// src/pages/LoginPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { login as loginApi } from "../services/api";

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const LoginPage: React.FC = () => {
  const navigate = useNavigate();

  // SOLO desarrollo; en producción déjalos vacíos 
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(Boolean(localStorage.getItem("remember_user")));
  const [capsOn, setCapsOn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const canSubmit = useMemo(
    () => username.trim().length > 3 && isValidEmail(username) && password.length >= 4 && !loading,
    [username, password, loading]
  );

  useEffect(() => {
    (document.getElementById("email-input") as HTMLInputElement | null)?.focus();
  }, []);

  // Bloquea scroll del body en esta pantalla
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleCaps = (e: React.KeyboardEvent<HTMLInputElement>) => {
    try {
      setCapsOn(e.getModifierState && e.getModifierState("CapsLock"));
    } catch {}
  };
  useEffect(() => {
    if (sessionStorage.getItem("token")) {
      navigate("/admin");
    }
  }, [navigate]);
  
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setLoading(true);
    try {
      await loginApi(username, password, remember);
      if (remember) localStorage.setItem("remember_user", username);
      else localStorage.removeItem("remember_user");
      navigate("/admin");
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Error al iniciar sesión. Verifica tu conexión e intenta de nuevo.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100svh-56px)] md:min-h-[calc(100dvh-64px)]">

      {/* Fondo full-screen (se DEFORMA para cubrir todo con object-fill).
          Si no quieres deformación, usa object-cover */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <picture>
          <source
            srcSet="/images/login-hero-768.webp 768w, /images/login-hero-1280.webp 1280w, /images/login-hero.webp 1920w"
            sizes="(max-width: 768px) 768px, (max-width: 1280px) 1280px, 1920px"
            type="image/webp"
          />
          <img
            src="/images/login-hero.webp"
            alt=""
            className="h-full w-full object-fill"
            loading="eager"
          />
        </picture>
        {/* ← se deforma para llenar. Cambia a 'object-cover' si lo prefieres */}

        {/* Capa de oscurecimiento + gradiente para legibilidad */}
        <div className="absolute inset-0 bg-slate-950/55" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/35 via-transparent to-black/30" />

        {/* Sutil máscara para dar profundidad en esquinas */}
        <div className="absolute inset-0 [mask-image:radial-gradient(1200px_600px_at_20%_10%,black,transparent)]" />
      </div>

      {/* Contenido centrado responsivo */}
      <div
        className={`
          mx-auto grid max-w-7xl grid-cols-1
          place-items-center px-4 py-8 sm:px-6 lg:grid-cols-12 lg:py-12
        `}
      >
        {/* Columna “branding” (solo en pantallas grandes) */}
        <div className="hidden lg:col-span-6 lg:block">
          <div className="max-w-xl text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)]">
            <h2 className="text-4xl font-extrabold leading-tight">Panel de Administración</h2>
            <p className="mt-4 text-lg/7 text-white/85">
              Controla OdeS y gastos con seguridad y rapidez. Inicia sesión para continuar.
            </p>
          </div>
        </div>

        {/* Card de Login */}
        <div className="w-full max-w-md lg:col-span-6">
          <div
            className="
              rounded-2xl bg-white/85 backdrop-blur-md
              shadow-[0_20px_60px_-15px_rgba(0,0,0,0.45)]
              ring-1 ring-white/60
              p-6 sm:p-8
            "
            aria-live="polite"
          >
            {/* Logo/etiqueta opcional */}
            <div className="mb-4 flex items-center justify-center gap-3">
              {/* <img src='/logo.svg' alt='Logo' className='h-8 w-8' /> */}
              <span className="rounded-full bg-sky-600/10 px-3 py-1 text-xs font-semibold text-sky-700">
                Acceso seguro
              </span>
            </div>

            <div className="mb-6 text-center">
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Iniciar sesión</h1>
              <p className="mt-1 text-sm text-slate-600">Accede al panel de administración</p>
            </div>

            {error && (
              <div
                role="alert"
                className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                <span className="mt-0.5 inline-block">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <form className="space-y-4" onSubmit={handleLogin} noValidate>
              {/* Email */}
              <div>
                <label htmlFor="email-input" className="mb-1 block text-sm font-medium text-slate-700">
                  Correo
                </label>
                <div className="relative">
                  <input
                    id="email-input"
                    name="email"
                    autoComplete="username"
                    inputMode="email"
                    type="email"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="tucorreo@empresa.com"
                    className="
                      w-full rounded-lg border border-slate-300 bg-white
                      px-3 py-2 pr-10 text-slate-900 placeholder-slate-400 outline-none
                      focus:border-sky-600 focus:ring-2 focus:ring-sky-600/25
                    "
                    aria-invalid={!isValidEmail(username)}
                    aria-describedby="email-help"
                    required
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                    @
                  </span>
                </div>
                <div id="email-help" className="mt-1 text-xs text-slate-500">
                  Usa tu correo corporativo.
                </div>
                {!isValidEmail(username) && username.length > 0 && (
                  <p className="mt-1 text-xs text-red-600">Formato de correo inválido</p>
                )}
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="password-input" className="mb-1 block text-sm font-medium text-slate-700">
                    Contraseña
                  </label>
                  {capsOn && <span className="text-xs font-medium text-amber-700">Bloq Mayús activado</span>}
                </div>
                <div className="relative">
                  <input
                    id="password-input"
                    name="current-password"
                    autoComplete="current-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyUp={handleCaps}
                    onKeyDown={handleCaps}
                    placeholder="••••••••"
                    className="
                      w-full rounded-lg border border-slate-300 bg-white
                      px-3 py-2 pr-12 text-slate-900 placeholder-slate-400 outline-none
                      focus:border-sky-600 focus:ring-2 focus:ring-sky-600/25
                    "
                    required
                    minLength={4}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="
                      absolute inset-y-0 right-2 flex items-center rounded
                      px-2 text-xs font-medium text-slate-500 hover:text-slate-700
                    "
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    {showPassword ? "Ocultar" : "Ver"}
                  </button>
                </div>
              </div>

              {/* Recordarme / Recuperación */}
              <div className="flex items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-600"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  Recuérdame
                </label>
                <button
                  type="button"
                  className="text-sm font-medium text-sky-700 hover:underline"
                  onClick={() => alert("Implementar recuperación de contraseña")}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={!canSubmit}
                className="
                  mt-1 w-full rounded-lg bg-gradient-to-r from-sky-600 to-indigo-600
                  px-4 py-2.5 font-semibold text-white shadow-md
                  transition hover:from-sky-700 hover:to-indigo-700 focus:outline-none
                  focus:ring-4 focus:ring-sky-300/40
                  disabled:cursor-not-allowed disabled:opacity-60
                "
              >
                {loading ? "Entrando…" : "Entrar"}
              </button>
            </form>

            <div className="mt-6 text-center text-xs text-slate-500">
              © {new Date().getFullYear()} Maramesa · Registro de Gastos &amp; OdeS
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
